class NavigationEvent extends Event {
}

interface NavigationEventMap {
  currententrychange: NavigationEvent;
  navigate: NavigationEvent;
  navigateerror: NavigationEvent;
  navigatesuccess: NavigationEvent;
}

export interface NavigationInterface extends EventTarget {
  addEventListener<K extends keyof NavigationEventMap> (
    type: K,
    listener: ((
      this: NavigationInterface,
      ev: NavigationEventMap[K],
    ) => void) | EventListenerOrEventListenerObject | null,
    options?: AddEventListenerOptions | boolean
  ): void;

  removeEventListener<K extends keyof NavigationEventMap> (
    type: K,
    listener: ((
      this: NavigationInterface,
      ev: NavigationEventMap[K],
    ) => void) | EventListenerOrEventListenerObject | null,
    options?: boolean | EventListenerOptions,
  ): void
}

export class Navigation implements NavigationInterface {
  private events: Map<
  keyof NavigationEventMap,
  Map<
  ((this: NavigationInterface, ev: NavigationEvent) => void) | null,
  undefined | boolean | AddEventListenerOptions
  >
  > = new Map();

  constructor () {
    this.initEvents();
  }

  public addEventListener<K extends keyof NavigationEventMap> (
    type: K,
    listener: ((this: NavigationInterface, ev: NavigationEventMap[K]) => void) | null,
    options?: AddEventListenerOptions | boolean,
  ): void {
    if (type !== 'navigatesuccess') {
      throw new Error('Unsupported event type');
    }

    let listeners: Map<typeof listener, typeof options> = new Map();

    if (this.events.has(type)) {
      listeners = this.events.get(type) as typeof listeners;
    }

    listeners.set(listener, options);

    this.events.set(type, listeners);
  }

  removeEventListener<K extends keyof NavigationEventMap> (
    type: K,
    listener: (this: NavigationInterface, ev: NavigationEventMap[K]) => void,
    options?: boolean | EventListenerOptions,
  ): void {
    if (!this.events.has(type)) {
      return;
    }

    const listeners = this.events.get(type);

    if (listeners) {
      listeners.delete(listener);

      this.events.set(type, listeners);
    }
  }

  public dispatchEvent (event: NavigationEvent): boolean {
    if (!event.type) {
      return false;
    }

    if (!event.type || !this.events.has(event.type as keyof NavigationEventMap)) {
      return true;
    }

    for (const [ type, listeners ] of this.events.entries()) {
      if (type !== 'navigatesuccess') {
        continue;
      }

      for (const [ listener ] of listeners.entries()) {
        if (!listener) {
          continue;
        }

        listener.call(this, event);
      }
    }

    return false;
  }

  private initEvents (): void {
    let currentLocation = window.location.href;

    const interval = window.setInterval(() => {
      if (currentLocation === window.location.href) {
        return;
      }

      currentLocation = window.location.href;

      const event = new NavigationEvent('navigatesuccess', { cancelable: true });

      this.dispatchEvent(event);
    }, 100);

    window.addEventListener('beforeunload', () => {
      window.clearInterval(interval);
    });
  }
}
